import React, { useEffect, useMemo, useRef, useState } from "react";
import "./LocationFeeModal.css";
import { useLocationFee } from "../context/LocationFeeContext";

const PRICE_PER_KM = 150;

const formatFCFA = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 })
    .format(Number(n || 0))
    .replace(/\u202F/g, " ");

function toRad(x) {
  return (x * Math.PI) / 180;
}
function haversineKm(a, b) {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(s));
}

async function reverseGeocode(lat, lng, signal) {
  const url =
    `https://nominatim.openstreetmap.org/reverse?` +
    `lat=${encodeURIComponent(lat)}` +
    `&lon=${encodeURIComponent(lng)}` +
    `&format=json&addressdetails=1`;

  const res = await fetch(url, {
    signal,
    headers: { Accept: "application/json" },
  });

  if (!res.ok) throw new Error("reverse_failed");
  const data = await res.json();

  // display_name est souvent très bon, sinon on compose un titre court
  const a = data?.address || {};
  const short =
    a.neighbourhood ||
    a.suburb ||
    a.village ||
    a.town ||
    a.city ||
    a.county ||
    data?.name ||
    data?.display_name ||
    "Position actuelle";

  return {
    label: short,
    full: data?.display_name || short,
  };
}

export default function LocationFeeModal({ openD, onClose }) {
  const { origin, userPos, setUserPos, setDistanceKm, setDeliveryFee } = useLocationFee();

  const [loadingGPS, setLoadingGPS] = useState(false);
  const [err, setErr] = useState("");

  // 🔎 Recherche manuelle
  const [q, setQ] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // const preview = useMemo(() => {
  //   if (!userPos) return null;
  //   const km = haversineKm(origin, userPos);
  //   const billedKm = Math.max(1, Math.ceil(km)); // ✅ arrondi au km supérieur
  //   const fee = billedKm * PRICE_PER_KM;
  //   return { km, billedKm, fee };
  // }, [origin, userPos]);

  const preview = useMemo(() => {
  if (!userPos) return null;

  const km = haversineKm(origin, userPos);
  const billedKm = Math.max(1, Math.ceil(km));
  const rawFee = billedKm * PRICE_PER_KM;

  const fee =
    rawFee <= 1000 ? 1000 :
    rawFee <= 1500 ? 1500 :
    rawFee <= 2000 ? 2000 :
    2500;

  return { km, billedKm, fee, rawFee };
}, [origin, userPos]);

  // ✅ GPS
 const getLocation = () => {
  setErr("");
  setLoadingGPS(true);

  if (!navigator.geolocation) {
    setLoadingGPS(false);
    setErr("La géolocalisation n'est pas supportée par ce navigateur.");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      try {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        // annule toute requête précédente
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        // ✅ récupère un vrai nom (quartier/ville/rue…)
        const place = await reverseGeocode(lat, lng, abortRef.current.signal);

        setUserPos({
          lat,
          lng,
          label: place.label,          // ex: Tokoin
          fullLabel: place.full,       // ex: Tokoin, Lomé, Togo (optionnel)
          source: "gps",
        });
      } catch (e) {
        // si reverse échoue, on met un fallback propre
        setUserPos({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: "Position actuelle",
          source: "gps",
        });
      } finally {
        setLoadingGPS(false);
      }
    },
    () => {
      setLoadingGPS(false);
      setErr("Impossible d'obtenir la localisation (permission refusée ?).");
    },
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
  );
};

  // ✅ Recherche Nominatim (OpenStreetMap)
  useEffect(() => {
    if (!openD) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = q.trim();
    if (term.length < 3) {
      setResults([]);
      setSearchLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setErr("");
        setSearchLoading(true);

        // annule la requête précédente si l’utilisateur tape vite
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        // ⚠️ Nominatim: ajoute un "User-Agent" côté serveur normalement.
        // En front web ça marche souvent, mais si tu es bloqué, il faudra passer par ton backend.
        const url =
          `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(term)}` +
          `&format=json&addressdetails=1&limit=6`;

        const res = await fetch(url, {
          signal: abortRef.current.signal,
          headers: {
            "Accept": "application/json",
          },
        });

        if (!res.ok) throw new Error("search_failed");

        const json = await res.json();

        const mapped = (json || []).map((r) => ({
          id: `${r.place_id}`,
          label: r.display_name,
          lat: Number(r.lat),
          lng: Number(r.lon),
        }));

        setResults(mapped);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setErr("Recherche impossible. Réessayez.");
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [q, openD]);

  const chooseResult = (r) => {
    setUserPos({ lat: r.lat, lng: r.lng, label: r.label });
    setResults([]);
  };

  const confirm = () => {
    if (!preview) return;
    setDistanceKm(preview.km);
    setDeliveryFee(preview.fee);
    onClose?.();
  };

  // reset visuel quand on ferme
  useEffect(() => {
    if (!openD) {
      setErr("");
      setQ("");
      setResults([]);
      setSearchLoading(false);
    }
  }, [openD]);

  if (!openD) return null;

  return (
    <div
      className="lfm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="lfm-modal" role="dialog" aria-modal="true">
        <div className="lfm-handle" />

        <div className="lfm-header">
          <h3>Localisation & Livraison</h3>
          <button className="lfm-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>

        {/* <div className="lfm-card">
          <div className="lfm-label">DOLLAR STORE CASSABLANCA</div>
          <div className="lfm-value">{origin.label}</div> */}
          {/* <div className="lfm-muted">
            {origin.lat.toFixed(5)}, {origin.lng.toFixed(5)}
          </div> */}
        {/* </div> */}

        {/* 🔎 Recherche manuelle */}
        <div className="lfm-card">
          <div className="lfm-label" style={{ textAlign:'left'}}>Chercher une localisation</div>

          <div className="lfm-searchwrap">
            <input
              className="lfm-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ex: Agoè, Lomé / Quartier / Rue..."
              inputMode="text"
              autoComplete="off"
            />
            {searchLoading && <div className="lfm-spinner" aria-hidden="true" />}
          </div>

          {!!results.length && (
            <div className="lfm-results">
              {results.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  className="lfm-result"
                  onClick={() => chooseResult(r)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}

          <div className="lfm-sep" />

          <button className="lfm-primary" onClick={getLocation} disabled={loadingGPS}>
            {loadingGPS ? "Récupération..." : "Utiliser ma position actuelle"}
          </button>

          {err && <div className="lfm-error">{err}</div>}
        </div>

        {/* <div className="lfm-card">
          <div className="lfm-label">Votre localisation</div>

          {!userPos ? (
            <div className="lfm-muted">Aucune localisation sélectionnée.</div>
          ) : (
            <>
              <div className="lfm-value">{userPos.label || "Position sélectionnée"}</div> */}
              {/* <div className="lfm-muted">
                {userPos.lat.toFixed(5)}, {userPos.lng.toFixed(5)}
              </div> */}
            {/* </>
          )}
        </div> */}

        <div className="lfm-two-cols">
            <div className="lfm-card">
                <div className="lfm-label">DOLLAR STORE</div>
                <div className="lfm-value">{origin.label}</div>
            </div>

            <div className="lfm-card">
                <div className="lfm-label">Votre localisation</div>

                {!userPos ? (
                <div className="lfm-muted">Aucune localisation sélectionnée.</div>
                ) : (
                <>
                    <div className="lfm-value">{userPos.fullLabel || userPos.label || "Position sélectionnée"}</div>
                </>
                )}
            </div>
            </div>

        <div className="lfm-card">
          <div className="lfm-label" style={{ textAlign:'left'}}></div>

          {!preview ? (
            <div className="lfm-muted">Choisissez votre localisation pour calculer la distance.</div>
          ) : (
            <>
              {/* <div className="lfm-row">
                <span className="lfm-muted">Distance</span>
                <strong>{preview.km.toFixed(2)} km</strong>
              </div>
              <div className="lfm-row">
                <span className="lfm-muted">Facturé</span>
                <strong>
                  {preview.billedKm} km × {formatFCFA(PRICE_PER_KM)} FCFA
                </strong>
              </div> */}
              <div className="lfm-total">
                <span>Frais de livraison</span>
                <strong>{formatFCFA(preview.fee)} FCFA</strong>
              </div>
            </>
          )}
        </div>

        <div className="lfm-footer">
          <button className="lfm-ghost" onClick={onClose}>
            Annuler
          </button>
          <button className="lfm-confirm" onClick={confirm} disabled={!preview}>
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
}